/**
 * Router - Vanilla TypeScript SPA Router
 *
 * Handles client-side routing with History API
 * Features:
 * - Route matching with params (e.g., /company/:id)
 * - Navigation guards
 * - Link interception
 * - Browser back/forward support
 * - 404 handling
 */

import type {
	RouterConfig,
	RouteMatch,
	RouterState,
	NavigationOptions,
	RouteParams,
} from '@/types';
import { BaseView } from '../views/BaseView';

export class Router {
	/**
	 * Router configuration
	 */
	private config: Required<Omit<RouterConfig, 'beforeEach' | 'afterEach' | 'fallback'>> & {
		beforeEach?: RouterConfig['beforeEach'];
		afterEach?: RouterConfig['afterEach'];
		fallback?: RouterConfig['fallback'];
	};

	/**
	 * Current router state
	 */
	private state: RouterState = {
		currentRoute: null,
		previousRoute: null,
		isNavigating: false,
	};

	/**
	 * Current active view instance
	 */
	private currentView: BaseView | null = null;

	/**
	 * Mount container element
	 */
	private mountElement: HTMLElement | null = null;

	/**
	 * Constructor
	 */
	constructor(config: RouterConfig) {
		this.config = {
			routes: config.routes,
			base: (config.base !== null && config.base !== undefined && config.base !== '') ? config.base : '/',
			mountElement: config.mountElement !== null && config.mountElement !== undefined ? config.mountElement : '#app',
			scrollToTop: config.scrollToTop !== false,
			scrollBehavior: (config.scrollBehavior !== null && config.scrollBehavior !== undefined) ? config.scrollBehavior : 'auto',
			beforeEach: config.beforeEach,
			afterEach: config.afterEach,
			fallback: config.fallback,
		};

		this.init();
	}

	/**
	 * Initialize router
	 */
	private init(): void {
		// Get mount element
		if (typeof this.config.mountElement === 'string') {
			this.mountElement = document.querySelector(this.config.mountElement);
		} else {
			this.mountElement = this.config.mountElement;
		}

		if (!this.mountElement) {
			const elementStr = typeof this.config.mountElement === 'string'
				? this.config.mountElement
				: '[HTMLElement]';
			throw new Error(
				`Router mount element "${elementStr}" not found`
			);
		}

		// Set up event listeners
		this.setupEventListeners();

		// Handle initial route
		void this.handleRoute(window.location.pathname);
	}

	/**
	 * Set up event listeners
	 */
	private setupEventListeners(): void {
		// Handle browser back/forward
		window.addEventListener('popstate', this.onPopState.bind(this));

		// Intercept link clicks
		document.addEventListener('click', this.onLinkClick.bind(this));
	}

	/**
	 * Handle popstate event (browser back/forward)
	 */
	private onPopState(_event: PopStateEvent): void {
		void this.handleRoute(window.location.pathname, { skipGuards: false });
	}

	/**
	 * Handle link click event
	 */
	private onLinkClick(event: MouseEvent): void {
		// Only handle left clicks
		if (event.button !== 0) return;

		// Find anchor element
		const target = (event.target as HTMLElement).closest('a');
		if (!target) return;

		// Check if it's an internal link
		const href = target.getAttribute('href');
		if (href === null || href === '' || href.startsWith('http') || href.startsWith('//') || href.startsWith('#')) {
			return;
		}

		// Check for modifier keys
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}

		// Check for target attribute
		if (target.hasAttribute('target')) {
			return;
		}

		// Check if it's a download link
		if (target.hasAttribute('download')) {
			return;
		}

		// Check if it's a file download (by file extension)
		if (/\.(xlsx|xls|pdf|zip|doc|docx|csv|json|txt|png|jpg|jpeg|gif|svg)$/i.test(href)) {
			return;
		}

		// Prevent default and navigate
		event.preventDefault();
		void this.navigate(href);
	}

	/**
	 * Navigate to a path
	 */
	async navigate(path: string, options: NavigationOptions = {}): Promise<boolean> {
		// Normalize path
		const normalizedPath = this.normalizePath(path);

		// Check if already on this route
		if (this.state.currentRoute?.path === normalizedPath && options.replace !== true) {
			return false;
		}

		// Handle route
		const success = await this.handleRoute(normalizedPath, options);

		if (success === true) {
			// Update history
			const url = this.getFullUrl(normalizedPath);
			if (options.replace === true) {
				window.history.replaceState((options.state !== null && options.state !== undefined) ? options.state : {}, '', url);
			} else {
				window.history.pushState((options.state !== null && options.state !== undefined) ? options.state : {}, '', url);
			}
		}

		return success;
	}

	/**
	 * Handle route navigation
	 */
	private async handleRoute(
		path: string,
		options: NavigationOptions = {}
	): Promise<boolean> {
		// Prevent concurrent navigation
		if (this.state.isNavigating) {
			return false;
		}

		this.state.isNavigating = true;

		try {
			// Normalize path
			const normalizedPath = this.normalizePath(path);

			// Match route
			const match = this.matchRoute(normalizedPath);

			if (!match) {
				// No match - try fallback
				if (this.config.fallback) {
					const fallbackMatch: RouteMatch = {
						route: this.config.fallback,
						params: {},
						query: new URLSearchParams(window.location.search),
						path: normalizedPath,
					};
					return await this.activateRoute(fallbackMatch, options);
				}

				console.warn(`No route matched for path: ${normalizedPath}`);
				return false;
			}

			// Run navigation guards
			if (options.skipGuards !== true) {
				const canNavigate = await this.runGuards(match);
				if (canNavigate !== true) {
					return false;
				}
			}

			// Activate route
			return await this.activateRoute(match, options);
		} finally {
			this.state.isNavigating = false;
		}
	}

	/**
	 * Match a route from path
	 */
	private matchRoute(path: string): RouteMatch | null {
		for (const route of this.config.routes) {
			const params = this.matchPath(route.path, path);
			if (params !== null) {
				return {
					route,
					params,
					query: new URLSearchParams(window.location.search),
					path,
				};
			}
		}
		return null;
	}

	/**
	 * Match a route path pattern against actual path
	 * Returns params if match, null otherwise
	 */
	private matchPath(pattern: string, path: string): RouteParams | null {
		// Exact match
		if (pattern === path) {
			return {};
		}

		// Build regex from pattern
		const paramNames: string[] = [];
		const regexPattern = pattern
			.replace(/:(\w+)/g, (_match: string, paramName: string) => {
				paramNames.push(paramName);
				return '([^/]+)';
			})
			.replace(/\//g, '\\/');

		const regex = new RegExp(`^${regexPattern}$`);
		const match = path.match(regex);

		if (!match) {
			return null;
		}

		// Extract params
		const params: RouteParams = {};
		paramNames.forEach((name, index) => {
			const value = match[index + 1];
			params[name] = (value !== null && value !== undefined) ? value : '';
		});

		return params;
	}

	/**
	 * Run navigation guards
	 */
	private async runGuards(match: RouteMatch): Promise<boolean> {
		const { route } = match;
		const { currentRoute } = this.state;

		// Global before guard
		if (this.config.beforeEach) {
			const result = await this.config.beforeEach(
				route,
				currentRoute?.route || null
			);
			if (!result) return false;
		}

		// Route-specific guard
		if (route.beforeEnter) {
			const result = await route.beforeEnter(route, currentRoute?.route || null);
			if (!result) return false;
		}

		return true;
	}

	/**
	 * Activate a matched route
	 */
	private async activateRoute(
		match: RouteMatch,
		_options: NavigationOptions
	): Promise<boolean> {
		// Unmount current view
		if (this.currentView) {
			await this.currentView.unmount();
			this.currentView = null;
		}

		// Update state
		this.state.previousRoute = this.state.currentRoute;
		this.state.currentRoute = match;

		// Create and mount new view
		try {
			const ViewClass = match.route.component;
			this.currentView = new ViewClass();
			this.currentView.setParams(match.params);
			this.currentView.setQuery(match.query);

			if (this.mountElement) {
				await this.currentView.mount(this.mountElement);
			}

			// Update document title
			const title = match.route.title;
			if (title !== null && title !== undefined && title !== '') {
				document.title = title;
			}

			// Update body attributes
			if (match.route.meta?.bodyClass !== null && match.route.meta?.bodyClass !== undefined && match.route.meta?.bodyClass !== '') {
				document.body.className = match.route.meta.bodyClass;
			}

			if (match.route.meta?.bodyData !== null && match.route.meta?.bodyData !== undefined) {
				for (const [key, value] of Object.entries(match.route.meta.bodyData)) {
					document.body.setAttribute(`data-${key}`, value);
				}
			}

			// Update sidebar active state
			if (match.route.sidebarItem !== null && match.route.sidebarItem !== undefined && match.route.sidebarItem !== '') {
				document.body.setAttribute('data-active-sidebar', match.route.sidebarItem);
			}

			// Scroll to top
			if (this.config.scrollToTop) {
				window.scrollTo({
					top: 0,
					behavior: this.config.scrollBehavior,
				});
			}

			// Call global after hook
			if (this.config.afterEach) {
				this.config.afterEach(match.route, this.state.previousRoute?.route || null);
			}

			return true;
		} catch (error) {
			console.error('Error activating route:', error);
			return false;
		}
	}

	/**
	 * Normalize a path (remove trailing slashes, etc.)
	 */
	private normalizePath(path: string): string {
		// Remove query string and hash
		const parts = path.split('?');
		const pathOnly = (parts[0] !== null && parts[0] !== undefined && parts[0] !== '') ? parts[0] : '/';
		const hashSplit = pathOnly.split('#')[0];
		let normalized = (hashSplit !== null && hashSplit !== undefined && hashSplit !== '') ? hashSplit : '/';

		// Remove trailing slash (except for root)
		if (normalized.length > 1 && normalized.endsWith('/')) {
			normalized = normalized.slice(0, -1);
		}

		// Ensure leading slash
		if (!normalized.startsWith('/')) {
			normalized = '/' + normalized;
		}

		return normalized;
	}

	/**
	 * Get full URL from path
	 */
	private getFullUrl(path: string): string {
		const base = this.config.base === '/' ? '' : this.config.base;
		return base + path;
	}

	/**
	 * Get current route
	 */
	getCurrentRoute(): RouteMatch | null {
		return this.state.currentRoute;
	}

	/**
	 * Get previous route
	 */
	getPreviousRoute(): RouteMatch | null {
		return this.state.previousRoute;
	}

	/**
	 * Go back in history
	 */
	back(): void {
		window.history.back();
	}

	/**
	 * Go forward in history
	 */
	forward(): void {
		window.history.forward();
	}

	/**
	 * Replace current route
	 */
	replace(path: string): Promise<boolean> {
		return this.navigate(path, { replace: true });
	}

	/**
	 * Destroy router
	 */
	destroy(): void {
		// Remove event listeners
		window.removeEventListener('popstate', this.onPopState.bind(this));
		document.removeEventListener('click', this.onLinkClick.bind(this));

		// Unmount current view
		if (this.currentView !== null) {
			void this.currentView.unmount();
			this.currentView = null;
		}
	}
}
